import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  alpha,
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  AccessTime,
  Web,
  Person,
  TrendingUp,
  Place,
  CalendarToday,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { apiService } from '../../services/apiService';
import { colors } from '../../theme/theme';
import { WebsiteInquiry } from './types';
import {
  StatCard,
  SearchBar,
  DATA_GRID_SX,
  tableCardSx,
  formatSubmittedDate,
  STAT_CARDS_GRID_SX,
  DATA_GRID_CONTAINER_SX,
  isThisMonth,
  fetchAllPages,
} from './shared';

export function WebsiteInquiriesTab() {
  const [inquiries, setInquiries] = useState<WebsiteInquiry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const allInquiries = await fetchAllPages<WebsiteInquiry>(async (page, pageSize) => {
        const r = await apiService.getWebsiteInquiries({ page, pageSize });
        return { total: r.total || 0, items: r.inquiries || [] };
      });

      allInquiries.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setInquiries(allInquiries);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch inquiries');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredInquiries = useMemo(() => {
    const searchLower = searchTerm.toLowerCase().trim();
    return inquiries.filter(
      (inquiry) =>
        searchLower === '' ||
        inquiry.fullName?.toLowerCase().includes(searchLower) ||
        inquiry.email?.toLowerCase().includes(searchLower) ||
        inquiry.phoneNumber?.toLowerCase().includes(searchLower) ||
        inquiry.location?.toLowerCase().includes(searchLower) ||
        inquiry.source?.toLowerCase().includes(searchLower)
    );
  }, [inquiries, searchTerm]);

  const columns: GridColDef[] = [
    {
      field: 'fullName',
      headerName: 'Full Name',
      width: 220,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: alpha(colors.primary, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Person sx={{ fontSize: 20, color: colors.primary }} />
          </Box>
          <Typography
            variant="body2"
            fontWeight="600"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: colors.textPrimary,
            }}
          >
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
          <Email sx={{ fontSize: 18, color: colors.info }} />
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: colors.textPrimary,
            }}
          >
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      width: 180,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
          <Phone sx={{ fontSize: 18, color: colors.success }} />
          <Typography variant="body2" color={colors.textPrimary} fontWeight="500">
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 220,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
          {params.value ? (
            <>
              <LocationOn sx={{ fontSize: 18, color: colors.warning }} />
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: colors.textPrimary,
                }}
              >
                {params.value}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color={colors.textSecondary} fontStyle="italic">
              Not provided
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 160,
      renderCell: (params) => (
        <Box sx={{ py: 1 }}>
          {params.value ? (
            <Chip
              label={params.value.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
              size="small"
              icon={<Web sx={{ fontSize: 14 }} />}
              sx={{
                backgroundColor: alpha(colors.info, 0.1),
                color: colors.info,
                border: `1px solid ${alpha(colors.info, 0.3)}`,
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 28,
                '& .MuiChip-icon': { color: colors.info },
              }}
            />
          ) : (
            <Typography variant="body2" color={colors.textSecondary} fontStyle="italic">
              N/A
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Submitted On',
      width: 180,
      renderCell: (params) => {
        const { date, time } = formatSubmittedDate(params.value);
        return (
          <Box display="flex" alignItems="center" gap={1} sx={{ py: 1 }}>
            <AccessTime sx={{ fontSize: 18, color: colors.textSecondary }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {date}
              </Typography>
              <Typography variant="caption" color={colors.textSecondary} display="block">
                {time}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const totalInquiries = inquiries.length;
  const inquiriesWithLocation = inquiries.filter((i) => i.location).length;
  const inquiriesThisMonth = inquiries.filter((i) => isThisMonth(i.createdAt)).length;

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={STAT_CARDS_GRID_SX}>
        <StatCard
          label="Total Inquiries"
          value={totalInquiries}
          icon={<TrendingUp sx={{ fontSize: 28, color: colors.primary }} />}
          color={colors.primary}
        />
        <StatCard
          label="With Location"
          value={inquiriesWithLocation}
          icon={<Place sx={{ fontSize: 28, color: colors.success }} />}
          color={colors.success}
        />
        <StatCard
          label="This Month"
          value={inquiriesThisMonth}
          icon={<CalendarToday sx={{ fontSize: 28, color: colors.info }} />}
          color={colors.info}
        />
      </Box>

      <SearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search by name, email, phone, location, or source..."
      />

      <Card sx={tableCardSx}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={DATA_GRID_CONTAINER_SX}>
            <DataGrid
              rows={filteredInquiries}
              columns={columns}
              getRowId={(row) => row._id}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              getRowHeight={() => 75}
              sx={DATA_GRID_SX}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
